import React, { lazy, Suspense } from "react";
import Bugsnag from "@bugsnag/js";
import Root from "./root";
import {
  failCalendarEntry,
  measureCalendarModule,
  trackCalendarEntries,
} from "./utils/calendar-entry-performance";

import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
  useRouteError,
} from "react-router-dom";
// Statically imported (not lazy): both route guards must resolve the server
// rollout mode before either Insights bundle mounts.
import {
  InsightsPreviewRoute,
  InsightsV2LegacyRoute,
} from "./pages/insights-preview/insights-preview-routes";
import { ExternalApprovalsRoute } from "./pages/approvals/external-approvals-route";
const HomePage = lazy(() =>
  import("./pages/home").then((m) => ({ default: m.HomePage }))
);

// Lazy-loaded components - Tier 3 (secondary routes, previously eager)
const LibraryPage = lazy(() =>
  import("./pages/library").then((m) => ({ default: m.LibraryPage }))
);
const PostsScreen = lazy(() => import("./components/Posts/PostsScreen"));
const InvoicesPage = lazy(() =>
  import("./pages/profile-settings/billing/invoices-page").then((m) => ({
    default: m.InvoicesPage,
  }))
);

// Lazy-loaded components - Tier 1 (largest/least frequently used)
const IdeaPlannerApp = lazy(() =>
  import("./@idea-planner/app").then((m) => ({ default: m.App }))
);
const InboxApp = lazy(() =>
  import("./shared/inbox/legacy-inbox-redirect").then((m) => ({
    default: m.LegacyInboxRedirect,
  }))
);
const InboxV2App = lazy(() =>
  import("./@inbox-v2/index").then((m) => ({ default: m.InboxV2App }))
);
const InboxV2ReplyTemplatesPage = lazy(() =>
  import("./@inbox-v2/index").then((m) => ({
    default: m.InboxV2ReplyTemplatesPage,
  }))
);
const InboxV2TagsPage = lazy(() =>
  import("./@inbox-v2/index").then((m) => ({ default: m.InboxV2TagsPage }))
);
const WorkshopApp = lazy(() =>
  import("./@workshop/app").then((m) => ({ default: m.WorkshopApp }))
);
const InboxInsightsPage = lazy(() => import("./pages/inbox-insights"));
const InsightsApp = lazy(() =>
  import("./@insights/index").then((m) => ({ default: m.InsightsApp }))
);
// Frozen v1 Insights, revived from `origin/master` as `@insights-legacy`. It is
// the DEFAULT experience, mounted at `/dashboard/insights-v2/*`. The modern v2
// design (`@insights`) moves to `/dashboard/insights-preview/*` behind a flag.
const InsightsLegacyApp = lazy(() =>
  import("./@insights-legacy/index").then((m) => ({ default: m.InsightsApp }))
);
const AdminApp = lazy(() =>
  import("./@admin/index").then((m) => ({ default: m.AdminApp }))
);
const CalendarLegacyRoute = lazy(() =>
  measureCalendarModule(
    "legacy",
    () => import("./pages/calendar-uplift/calendar-legacy-route")
  ).then((m) => ({
    default: m.CalendarLegacyRoute,
  }))
);
const CalendarUpliftRoute = lazy(() =>
  measureCalendarModule(
    "new",
    () => import("./pages/calendar-uplift/calendar-uplift-route")
  ).then((m) => ({
    default: m.CalendarUpliftRoute,
  }))
);

// Lazy-loaded components - Tier 2 (medium size)
const ProfileSettings = lazy(
  () => import("./pages/profile-settings/profile-settings")
);
const UsersPage = lazy(() =>
  import("./pages/users").then((m) => ({ default: m.UsersPage }))
);
const AccountsPage = lazy(() =>
  import("./pages/accounts/page").then((m) => ({ default: m.AccountsPage }))
);
const CesdkPage = lazy(() =>
  import("./pages/cesdk").then((m) => ({ default: m.CesdkPage }))
);
const BrandKitsPage = lazy(() =>
  import("./features/brand-kits").then((m) => ({ default: m.BrandKitsPage }))
);
const StyleGuidePage = lazy(() =>
  import("./pages/style-guide/style-guide-page").then((m) => ({
    default: m.StyleGuidePage,
  }))
);
const GridDesignerPage = lazy(() =>
  import("./pages/grid-designer").then((m) => ({ default: m.GridDesignerPage }))
);
const InstagramPublishingPage = lazy(() =>
  import("./pages/instagram-publishing").then((m) => ({
    default: m.InstagramPublishingPage,
  }))
);
const NotFoundPage = lazy(() =>
  import("./pages/not-found").then((m) => ({ default: m.NotFoundPage }))
);
const ApprovalsPage = lazy(() =>
  import("./pages/approvals").then((m) => ({ default: m.ApprovalsPage }))
);
const ApprovalsBoardPage = lazy(() =>
  import("./pages/approvals/board").then((m) => ({
    default: m.ApprovalsBoardPage,
  }))
);
// Content Collect (CC-17 / SKE-4274). Flat siblings under Library rather than
// nested children: the Library page is redux-page-state driven, so a nested
// outlet would have to re-init the whole media-library shell.
const CollectListPage = lazy(() =>
  import("./pages/collect").then((m) => ({ default: m.CollectListPage }))
);
const CollectNewPage = lazy(() =>
  import("./pages/collect").then((m) => ({ default: m.CollectNewPage }))
);
const CollectDetailPage = lazy(() =>
  import("./pages/collect").then((m) => ({ default: m.CollectDetailPage }))
);
const CollectEditPage = lazy(() =>
  import("./pages/collect").then((m) => ({ default: m.CollectEditPage }))
);

import HomeIcon from "./pages/component/menu/icons/home-03.svg";
import LibIcon from "./pages/component/menu/icons/library-v2.svg";
import TemplatesIcon from "./pages/component/menu/icons/templates.svg";
import PlannerIcon from "./pages/component/menu/icons/planner.svg";
import CalendarIcon from "./pages/component/menu/icons/calendar.svg";
import BulkUploadIcon from "./pages/component/menu/icons/bulk-upload.svg";
import UpcomingIcon from "./pages/component/menu/icons/upcoming.svg";
import DraftsIcon from "./pages/component/menu/icons/drafts.svg";
import ArchiveIcon from "./pages/component/menu/icons/archive.svg";
import InsightsIcon from "./pages/component/menu/icons/insights.svg";
import SocialAccountsIcon from "./pages/component/menu/icons/social-accounts.svg";
import UsersIcon from "./pages/component/menu/icons/users.svg";
import SettingsIcon from "./pages/component/menu/icons/settings.svg";
import LabelsIcon from "./pages/component/menu/icons/labels-icon.svg";
import IdeaPlannerIcon from "./pages/component/menu/icons/workshop.svg";
import ExternalPreviewIcon from "./pages/component/menu/icons/external-preview.svg";
import { PAGES } from "./constants/Pages";
import { buildPostsListRoute } from "./components/Posts/posts-deep-link";
import { formBaseTheme } from "./ui/ecosystems/forms/theme";
import { ThemeProvider } from "styled-components";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { InstagramCampaignRouteGuard } from "./features/instagram-campaign/campaign-route-guard";
const PostTemplatesPage = lazy(() =>
  import("./features/post-templates").then((m) => ({
    default: m.PostTemplatesPage,
  }))
);
const PlannerPage = lazy(() =>
  import("./pages/planner/planner-page").then((m) => ({
    default: m.PlannerPage,
  }))
);
import { BulkUploadRedirect } from "./pages/bulk-upload-redirect";
import { toError } from "./utils/to-error";

// Loading fallback for lazy-loaded routes. Never `h-screen`: the panel scrolls
// twice. `.lazy-route-fallback` in shadcn.css fills the space below the header.
// RouteErrorFallback keeps `h-screen` because it replaces Root, outside the panel.
const LoadingFallback = () => (
  <div className="lazy-route-fallback flex items-center justify-center">
    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
  </div>
);

// Carries the route shell, not `h-screen`, so the page never scrolls twice
// while the chunk loads (see `.grid-designer-route-shell` in shadcn.css).
const GridDesignerLoadingFallback = () => (
  <div className="grid-designer-route-shell flex items-center justify-center">
    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
  </div>
);

// Error fallback for lazy-loaded chunks that fail to load
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    failCalendarEntry("route_render");
    console.error("Lazy component loading error:", error, errorInfo);
  }

  retry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="lazy-route-fallback flex flex-col items-center justify-center gap-4">
          <i className="fa-solid fa-triangle-exclamation text-4xl text-red-500"></i>
          <h2 className="text-xl font-semibold text-neutral-900">
            Failed to load page
          </h2>
          <p className="text-sm text-neutral-600">
            There was an error loading this page. Please try again.
          </p>
          <button
            onClick={this.retry}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component for lazy-loaded routes with error boundary
// eslint-disable-next-line react/prop-types
const LazyRoute = ({ children, fallback = <LoadingFallback /> }) => (
  <LazyErrorBoundary>
    <Suspense fallback={fallback}>{children}</Suspense>
  </LazyErrorBoundary>
);

// Keeps existing links to `/dashboard/approvals` working after making the
// Approvals Board the default approvals surface.
const ApprovalsIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/dashboard/approvals/board${search}`} replace />;
};

// Redirect component that preserves query params and wildcard sub-paths

const RedirectWithParams = ({ to }) => {
  const { search } = useLocation();
  const { "*": splat } = useParams();
  const path = splat ? `${to}/${splat}` : to;
  return <Navigate to={`${path}${search}`} replace />;
};

const PostsRoute = ({ page, view = "list" }) => (
  <LazyRoute>
    <PostsScreen page={page} view={view} modern />
  </LazyRoute>
);

// The Insights breadcrumb sub-label, shared by the two mounts of the app
// (`/dashboard/insights-v2/*` and the `/dashboard/insights-preview/*` preview),
// which differ only in their `base` path. `base` must end with a trailing slash.
const insightsSubPageLabel = (pathname, base) => {
  if (pathname.includes(`${base}social-listening/sentiment`))
    return "Sentiment";
  if (pathname.includes(`${base}social-listening/competitors`))
    return "Competitors";
  if (pathname.includes(`${base}social-listening/keyword`)) return "Keywords";
  if (pathname.includes(`${base}social-listening/audience`)) return "Audience";
  if (pathname.includes(`${base}social-listening`)) return "Summary";
  if (pathname.includes(`${base}labels`)) return "Label Performance";
  if (pathname.includes(`${base}ads`)) return "Paid Social";
  if (pathname.includes(`${base}report-builder`)) return "Custom Reports";
  if (pathname.includes(`${base}website-traffic`)) return "Website Traffic";
  if (pathname.includes(`${base}tag-health`)) return "Tag Health";
  // Track the analytics sub-page (Content, Reels, Audience, …); only the
  // overview keeps the roll-up "Brand Summary" name.
  const analyticsPage = pathname.match(/\/analytics\/([^/?]+)/)?.[1];
  if (analyticsPage && analyticsPage !== "overview") {
    return analyticsPage
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return "Brand Summary";
};

const PostsPathRedirect = () => {
  const { postId } = useParams();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  if (postId) {
    searchParams.set("postId", postId);
  }

  return (
    <Navigate
      to={buildPostsListRoute(PAGES.UPCOMING, searchParams.toString())}
      replace
    />
  );
};

const DashboardWrapper = ({ element, ...props }) => {
  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <ThemeProvider theme={formBaseTheme}>
        {React.cloneElement(element, { ...props })}
      </ThemeProvider>
    </QueryParamProvider>
  );
};

// NOTE: (Andrey): ncyBreadcrumb - angular router apendix, need avoid in future

// Top-level error element for react-router. Catches unhandled errors in route
// loaders, actions, and render paths that escape component-level boundaries.
const RouteErrorFallback = () => {
  const error = useRouteError();

  if (Bugsnag.isStarted?.()) {
    Bugsnag.notify?.(toError(error));
  }
  console.error("[RouteErrorFallback]", error);

  return (
    <div className="flex flex-col items-center justify-center h-screen gap-4 px-4">
      <i className="fa-solid fa-triangle-exclamation text-4xl text-red-500"></i>
      <h2 className="text-xl font-semibold text-neutral-900">
        Something went wrong
      </h2>
      <p className="text-sm text-neutral-600 text-center max-w-md">
        An unexpected error occurred. Please try reloading the page. If the
        problem persists, contact support.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
      >
        Reload Page
      </button>
    </div>
  );
};

const routes = [
  {
    path: "/",
    element: <DashboardWrapper element={<Root />} />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: "dashboard",
        element: (
          <LazyRoute>
            <HomePage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Home",
          icon: <HomeIcon />,
        },
        params: { title: "Home" },
      },
      {
        path: "dashboard/onboarding/:onboardingStage",
        element: (
          <LazyRoute>
            <HomePage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Home",
          icon: <HomeIcon />,
        },
        params: { title: "Home" },
      },
      {
        path: "dashboard/inbox/*",
        element: (
          <LazyRoute>
            <InboxApp />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
          customLinkInTitle: "report_inbox_bug",
        },
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2",
        element: (
          <LazyRoute>
            <InboxV2App />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
        },
        headerVariant: "inbox-v2",
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2/board/:boardId",
        element: (
          <LazyRoute>
            <InboxV2App />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
        },
        headerVariant: "inbox-v2",
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2/temporary-board",
        element: (
          <LazyRoute>
            <InboxV2App />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
        },
        headerVariant: "inbox-v2",
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2/conversation/:conversationId",
        element: (
          <LazyRoute>
            <InboxV2App />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
        },
        headerVariant: "inbox-v2",
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2/reply-templates",
        element: (
          <LazyRoute>
            <InboxV2ReplyTemplatesPage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Reply Templates",
        },
        params: { title: "Reply Templates" },
      },
      {
        path: "dashboard/inbox-v2/tags",
        element: (
          <LazyRoute>
            <InboxV2TagsPage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Tags",
        },
        params: { title: "Tags" },
      },
      {
        path: "dashboard/workshop/labels",
        element: (
          <LazyRoute>
            <WorkshopApp />
          </LazyRoute>
        ),
        params: { title: "Workshop" },
        ncyBreadcrumb: {
          label: "Labels",
          icon: <LabelsIcon />,
        },
      },
      {
        path: "dashboard/idea-planner",
        element: (
          <LazyRoute>
            <IdeaPlannerApp key={"singleton-idea-planner-app"} />
          </LazyRoute>
        ),
        params: { title: "Ideas" },
        ncyBreadcrumb: {
          label: "Ideas",
          icon: <IdeaPlannerIcon />,
        },
      },
      {
        path: "dashboard/library",
        element: (
          <LazyRoute>
            <LibraryPage />
          </LazyRoute>
        ),
        params: { title: "Library" },
        ncyBreadcrumb: {
          label: "Library",
          icon: <LibIcon />,
        },
      },
      {
        path: "dashboard/library/collect",
        element: (
          <LazyRoute>
            <CollectListPage />
          </LazyRoute>
        ),
        params: { title: "Content Collect" },
        ncyBreadcrumb: {
          label: "Library",
          subLabel: "Content Collect",
          icon: <LibIcon />,
        },
      },
      {
        path: "dashboard/library/collect/new",
        element: (
          <LazyRoute>
            <CollectNewPage />
          </LazyRoute>
        ),
        params: { title: "Content Collect" },
        ncyBreadcrumb: {
          label: "Library",
          subLabel: "Content Collect",
          icon: <LibIcon />,
        },
      },
      {
        path: "dashboard/library/collect/:collectId",
        element: (
          <LazyRoute>
            <CollectDetailPage />
          </LazyRoute>
        ),
        params: { title: "Content Collect" },
        ncyBreadcrumb: {
          label: "Library",
          subLabel: "Content Collect",
          icon: <LibIcon />,
        },
      },
      {
        path: "dashboard/library/collect/:collectId/edit",
        element: (
          <LazyRoute>
            <CollectEditPage />
          </LazyRoute>
        ),
        params: { title: "Content Collect" },
        ncyBreadcrumb: {
          label: "Library",
          subLabel: "Content Collect",
          icon: <LibIcon />,
        },
      },
      {
        path: "dashboard/calendar",
        element: (
          <LazyRoute>
            <CalendarLegacyRoute />
          </LazyRoute>
        ),
        params: { title: "Calendar" },
        ncyBreadcrumb: {
          label: "Calendar",
          icon: <CalendarIcon />,
        },
      },
      {
        path: "dashboard/calendar-new",
        headerVariant: "calendar-uplift",
        element: (
          <LazyRoute>
            <CalendarUpliftRoute />
          </LazyRoute>
        ),
        params: { title: "Calendar" },
        ncyBreadcrumb: {
          label: "Calendar",
          icon: <CalendarIcon />,
        },
      },
      {
        path: "dashboard/accounts",
        element: (
          <LazyRoute>
            <AccountsPage />
          </LazyRoute>
        ),
        params: { title: "Manage Accounts" },
        ncyBreadcrumb: {
          label: "Manage Accounts",
          icon: <SocialAccountsIcon />,
        },
      },
      {
        path: "dashboard/groups",
        element: (
          <LazyRoute>
            <AccountsPage />
          </LazyRoute>
        ),
        params: { title: "Manage Groups" },
        ncyBreadcrumb: {
          label: "Manage Accounts",
          icon: <SocialAccountsIcon />,
        },
      },
      {
        path: "dashboard/accounts/:accountId",
        element: (
          <LazyRoute>
            <AccountsPage />
          </LazyRoute>
        ),
        params: { title: "Manage Accounts" },
        ncyBreadcrumb: {
          label: "Manage Accounts",
          icon: <SocialAccountsIcon />,
        },
      },
      {
        path: "dashboard/groups/:groupId",
        element: (
          <LazyRoute>
            <AccountsPage />
          </LazyRoute>
        ),
        params: { title: "Manage Groups" },
        ncyBreadcrumb: {
          label: "Manage Accounts",
          icon: <SocialAccountsIcon />,
        },
      },
      {
        path: "dashboard/users",
        element: (
          <LazyRoute>
            <UsersPage />
          </LazyRoute>
        ),
        params: { title: "Users" },
        ncyBreadcrumb: {
          label: "Users",
          icon: <UsersIcon />,
        },
      },
      {
        path: "dashboard/approvals",
        element: <ApprovalsIndexRedirect />,
      },
      {
        path: "dashboard/approvals/board",
        element: (
          <LazyRoute>
            <ApprovalsBoardPage />
          </LazyRoute>
        ),
        params: { title: "Approvals" },
        ncyBreadcrumb: {
          label: "Approvals",
          subLabel: "Board",
          icon: <ExternalPreviewIcon />,
        },
      },
      {
        path: "dashboard/approvals/links-themes",
        element: (
          <ExternalApprovalsRoute>
            <LazyRoute>
              <ApprovalsPage />
            </LazyRoute>
          </ExternalApprovalsRoute>
        ),
        params: { title: "Approvals" },
        ncyBreadcrumb: {
          label: "Approvals",
          icon: <ExternalPreviewIcon />,
        },
      },
      {
        path: "dashboard/posts/:postId",
        element: <PostsPathRedirect />,
      },
      {
        path: "dashboard/schedules/list/active",
        element: <PostsRoute page={PAGES.UPCOMING} />,
        params: { title: "Posts" },
        ncyBreadcrumb: {
          label: "Posts",
          subLabel: "Upcoming",
          icon: <UpcomingIcon />,
        },
      },
      {
        path: "dashboard/schedules/list/inactive",
        element: <PostsRoute page={PAGES.ARCHIVE} />,
        params: { title: "Posts" },
        ncyBreadcrumb: {
          label: "Posts",
          subLabel: "Archive",
          icon: <ArchiveIcon />,
        },
      },
      {
        path: "dashboard/schedules/list/drafts",
        element: <PostsRoute page={PAGES.DRAFT} />,
        params: { title: "Posts" },
        ncyBreadcrumb: {
          label: "Posts",
          subLabel: "Drafts",
          icon: <DraftsIcon />,
        },
      },
      {
        path: "dashboard/schedules/table/active",
        element: <PostsRoute page={PAGES.UPCOMING} view="table" />,
        params: { title: "Posts" },
        ncyBreadcrumb: {
          label: "Posts",
          icon: <UpcomingIcon />,
        },
      },
      {
        path: "dashboard/schedules/table/inactive",
        element: <PostsRoute page={PAGES.ARCHIVE} view="table" />,
        params: { title: "Posts" },
        ncyBreadcrumb: {
          label: "Posts",
          icon: <ArchiveIcon />,
        },
      },
      {
        path: "dashboard/schedules/table/drafts",
        element: <PostsRoute page={PAGES.DRAFT} view="table" />,
        params: { title: "Posts" },
        ncyBreadcrumb: {
          label: "Posts",
          icon: <DraftsIcon />,
        },
      },
      {
        path: "dashboard/schedules/dashboard/active",
        element: <RedirectWithParams to="/dashboard/schedules/list/active" />,
      },
      {
        path: "dashboard/schedules/dashboard/inactive",
        element: <RedirectWithParams to="/dashboard/schedules/list/inactive" />,
      },
      {
        path: "dashboard/schedules/dashboard/drafts",
        element: <RedirectWithParams to="/dashboard/schedules/list/drafts" />,
      },
      {
        path: "dashboard/contact",
        element: <Navigate to="/dashboard/" replace />,
      },
      {
        path: "dashboard/profile",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Profile Settings" },
        ncyBreadcrumb: {
          label: "Profile",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/subscription",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Subscription" },
        ncyBreadcrumb: {
          label: "Subscription",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/collaborate",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Collaborate settings" },
        ncyBreadcrumb: {
          label: "Collaborate Settings",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/profile/settings/meta-ads",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Meta Ads Settings" },
        ncyBreadcrumb: {
          label: "Meta Ads Settings",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/profile/settings/mcp",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Sked MCP" },
        ncyBreadcrumb: {
          label: "Sked MCP",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/change-password",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Password" },
        ncyBreadcrumb: {
          label: "Password",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/invoices",
        element: (
          <LazyRoute>
            <InvoicesPage />
          </LazyRoute>
        ),
        params: { title: "Invoices" },
        ncyBreadcrumb: {
          label: "Invoices",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/referral",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Referral" },
        ncyBreadcrumb: {
          label: "Referral",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/profile/2faRegistration",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Two-Factor Authentication" },
        ncyBreadcrumb: {
          label: "Two-Factor Authentication",
          icon: <SettingsIcon />,
        },
      },
      // Canonical deep-link for every profile-settings section.
      // `:section` is a slug (e.g. "sessions", "billing", "team-collaboration")
      // resolved by profile-settings.tsx. Legacy per-section routes above
      // still work (bookmarks, external links) and take precedence when they
      // match more specifically.
      {
        path: "dashboard/profile/:section",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Profile Settings" },
        ncyBreadcrumb: {
          label: "Profile",
          icon: <SettingsIcon />,
        },
      },
      // Deep-link into a section's sub-view. Only the Integrations hub interprets
      // `:subPage` today (a provider key → connection detail, SKE-3972); other
      // sections ignore it and render the same as `/dashboard/profile/:section`.
      {
        path: "dashboard/profile/:section/:subPage",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Profile Settings" },
        ncyBreadcrumb: {
          label: "Profile",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/template-manager",
        element: (
          <LazyRoute>
            <PostTemplatesPage />
          </LazyRoute>
        ),
        params: { title: "Template Manager" },
        ncyBreadcrumb: {
          label: "Template Manager",
          icon: <TemplatesIcon />,
        },
      },
      // Planner routes
      // grid-preview and planner both use the same page
      // can retire planner route in future if needed
      {
        path: "dashboard/grid-preview",
        element: (
          <LazyRoute>
            <PlannerPage />
          </LazyRoute>
        ),
        params: { title: "Grid Preview" },
        ncyBreadcrumb: {
          label: "Grid Preview",
          icon: <PlannerIcon />,
        },
      },
      {
        path: "dashboard/planner",
        element: <Navigate to="/dashboard/grid-preview" replace />,
      },
      // These legacy `/dashboard/insights/*` redirects keep targeting insights-v2;
      // an opted-in preview user is then forwarded on by the InsightsV2LegacyRoute
      // guard, so the chain lands them on the matching preview subpath.
      {
        path: "dashboard/insights/analytics/audience",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/analytics/audience/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/analytics/competitors",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/competitors" />
        ),
      },
      {
        path: "dashboard/insights/analytics/competitors/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/competitors" />
        ),
      },
      {
        path: "dashboard/insights/audience",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/audience/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/ads",
        element: <RedirectWithParams to="/dashboard/insights-v2/ads" />,
      },
      {
        path: "dashboard/insights/labels",
        element: <RedirectWithParams to="/dashboard/insights-v2/labels" />,
      },
      {
        path: "dashboard/insights/report-builder/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/report-builder" />
        ),
      },
      // Social Listening is served exclusively through the in-repo
      // `@insights` app (mounted at `/dashboard/insights-v2/*`). Legacy
      // `/dashboard/social-listening/*` URLs are redirected so old links
      // (emails, deep links, bookmarks) land on the modern experience.
      {
        path: "dashboard/social-listening/sentiment/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/sentiment" />
        ),
      },
      {
        path: "dashboard/social-listening/audience",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/audience" />
        ),
      },
      {
        path: "dashboard/social-listening/audience/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/audience" />
        ),
      },
      {
        path: "dashboard/social-listening/competitors/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/competitors" />
        ),
      },
      {
        path: "dashboard/social-listening/keyword/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/keyword" />
        ),
      },
      {
        path: "dashboard/social-listening/upgrade",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/upgrade" />
        ),
      },
      {
        path: "dashboard/social-listening/no-access",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/no-access" />
        ),
      },
      {
        path: "dashboard/social-listening/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/summary" />
        ),
      },
      {
        path: "dashboard/insights/inbox-metrics",
        headerVariant: "inbox-v2",
        element: (
          <LazyRoute>
            <InboxInsightsPage />
          </LazyRoute>
        ),
        params: { title: "Inbox Metrics" },
        ncyBreadcrumb: {
          icon: <InsightsIcon />,
          label: "Insights",
          subLabel: "Inbox Metrics",
        },
      },
      // DEFAULT: frozen v1 Insights (`@insights-legacy`) at `/dashboard/insights-v2/*`.
      // Wrapped in the legacy guard so an opted-in preview user is forwarded to
      // the matching `/dashboard/insights-preview/*` subpath (share links excepted).
      {
        path: "dashboard/insights-v2/*",
        element: (
          <InsightsV2LegacyRoute>
            <LazyRoute>
              <InsightsLegacyApp />
            </LazyRoute>
          </InsightsV2LegacyRoute>
        ),
        params: { title: "Insights" },
        ncyBreadcrumb: {
          icon: <InsightsIcon />,
          getLabel: (pathname) => {
            if (pathname.includes("/insights-v2/social-listening/audience")) {
              return "Insights";
            }
            if (pathname.includes("/insights-v2/social-listening")) {
              return "Social Listening";
            }
            return "Insights";
          },
          getSubLabel: (pathname) =>
            insightsSubPageLabel(pathname, "/dashboard/insights-v2/"),
        },
      },
      // PREVIEW: modern v2 Insights (`@insights`) at `/dashboard/insights-preview/*`,
      // reached through the server-controlled rollout. The wrapper mounts OUTSIDE
      // the lazy boundary so mode resolution and opt-in happen before chunk load.
      {
        path: "dashboard/insights-preview/*",
        element: (
          <InsightsPreviewRoute>
            <LazyRoute>
              <InsightsApp />
            </LazyRoute>
          </InsightsPreviewRoute>
        ),
        params: { title: "Insights" },
        ncyBreadcrumb: {
          icon: <InsightsIcon />,
          getLabel: (pathname) => {
            if (
              pathname.includes("/insights-preview/social-listening/audience")
            ) {
              return "Insights";
            }
            if (pathname.includes("/insights-preview/social-listening")) {
              return "Social Listening";
            }
            return "Insights";
          },
          getSubLabel: (pathname) =>
            insightsSubPageLabel(pathname, "/dashboard/insights-preview/"),
        },
      },
      {
        path: "dashboard/insights/*",
        element: <RedirectWithParams to="/dashboard/insights-v2" />,
      },
      {
        path: "dashboard/cesdk",
        element: (
          <LazyRoute>
            <CesdkPage />
          </LazyRoute>
        ),
        params: { title: "Create assets" },
        ncyBreadcrumb: {
          label: "Create assets",
          icon: <SettingsIcon />,
          subLabel: "Create assets",
        },
      },
      {
        path: "dashboard/brand-kits",
        element: (
          <Navigate to="/dashboard/brand-kits/brand-description" replace />
        ),
        params: { title: "Brand Kits - Brand Description" },
        ncyBreadcrumb: {
          label: "Brand Kits | Brand Description",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/brand-kits/brand-description",
        element: (
          <LazyRoute>
            <BrandKitsPage />
          </LazyRoute>
        ),
        params: { title: "Brand Kits - Brand Description" },
        ncyBreadcrumb: {
          label: "Brand Kits | Brand Description",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/brand-kits/tone-of-voice",
        element: (
          <LazyRoute>
            <BrandKitsPage />
          </LazyRoute>
        ),
        params: { title: "Brand Kits - Tone of Voice" },
        ncyBreadcrumb: {
          label: "Brand Kits | Tone of Voice",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/brand-kits/instructions",
        element: (
          <LazyRoute>
            <BrandKitsPage />
          </LazyRoute>
        ),
        params: { title: "Brand Kits - Instructions" },
        ncyBreadcrumb: {
          label: "Brand Kits | Instructions",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/upload",
        element: <BulkUploadRedirect />,
        params: { title: "Bulk Upload" },
        ncyBreadcrumb: {
          // we refresh accounts after page is loaded
          label: "Upload",
          icon: <BulkUploadIcon />,
        },
      },
      {
        path: "dashboard/bulk",
        element: <BulkUploadRedirect />,
        params: { title: "Bulk Upload" },
        ncyBreadcrumb: {
          label: "Upload",
          icon: <BulkUploadIcon />,
        },
      },
      // Hidden route (no menu entry) while the Grid Designer is validated
      // alongside the existing planner, the same pattern as
      // `dashboard/style-guide` and `admin/*`. The breadcrumb titles the shell
      // header, which is otherwise a blank band above the toolbar.
      {
        path: "dashboard/grid-designer",
        element: (
          <LazyRoute fallback={<GridDesignerLoadingFallback />}>
            <GridDesignerPage />
          </LazyRoute>
        ),
        params: { title: "Grid Designer" },
        ncyBreadcrumb: {
          label: "Grid Designer",
        },
      },
      {
        path: "dashboard/grid-designer/:gridId",
        element: (
          <LazyRoute fallback={<GridDesignerLoadingFallback />}>
            <GridDesignerPage />
          </LazyRoute>
        ),
        params: { title: "Grid Designer" },
        ncyBreadcrumb: {
          label: "Grid Designer",
        },
      },
      // Hidden route (no menu entry of its own here, no breadcrumb): the
      // campaign's sidebar entry is rendered by the campaign feature and only
      // while the campaign is on. SKE-4733: the ROUTE is guarded the same way
      // now, redirecting to Manage Accounts while the campaign is off for the
      // workspace, because the page's pass is refused server-side then too.
      //
      // A SIBLING under path "/", never nested inside another route.
      // `root.tsx` reads the second route matched against its location, so
      // a nested child blanks the page header. Commit 4d3a42e56c is the worked
      // example, and `dashboard/grid-designer` above is the same pattern.
      {
        path: "dashboard/instagram-publishing",
        element: (
          <InstagramCampaignRouteGuard fallback={<LoadingFallback />}>
            <LazyRoute>
              <InstagramPublishingPage />
            </LazyRoute>
          </InstagramCampaignRouteGuard>
        ),
        params: { title: "Update Instagram publishing" },
      },
      {
        path: "dashboard/style-guide",
        element: (
          <LazyRoute>
            <StyleGuidePage />
          </LazyRoute>
        ),
        params: { title: "Style Guide" },
      },
      {
        path: "admin/*",
        element: (
          <LazyRoute>
            <AdminApp />
          </LazyRoute>
        ),
        params: { title: "Admin" },
      },
      {
        path: "*",
        element: (
          <LazyRoute>
            <NotFoundPage />
          </LazyRoute>
        ),
        params: { title: "Page Not Found" },
      },
    ],
  },
];
const router = createBrowserRouter(routes);
const stopCalendarEntryTracking = trackCalendarEntries(router);
if (import.meta.hot) import.meta.hot.dispose(stopCalendarEntryTracking);

export { router, routes };
